import { useCallback, useMemo, useState } from 'react';
import { inject } from 'mobx-react';
import Dialog from '../dialog/dialog';
import './add-invoice-expense-modal.scss';
import MainInfo from '../../pages/review-expense/main-info/main-info';
import ExpenseComponent from '../../pages/review-expense/expense-component/expense-component';
import ExpenseReviewAlerts from '../../pages/review-expense/expense-review-alerts';
import useExpenseReviewForm from '../../pages/review-expense/use-expense-review-form';
import { resolvePrincipalUserIdFromPayloadUser } from '../../pages/review-expense/utils/contract-expense-json';
import { formatValidateExpenseError } from '../../utils/resolve-validation-message';
import { fetch } from '../../utils/api-wrapper';
import Utils from '../../utils/utils';

// Refusal codes of POST /invoices/<id>/expenses -> user-facing text.
const ERROR_KEYS = {
  expense_account_locked: 'invoice.add_expense.error.locked',
  invoice_changed: 'invoice.add_expense.error.changed',
  invoice_not_expense_account: 'invoice.add_expense.error.not_expense_account',
  expense_account_not_found: 'invoice.add_expense.error.not_expense_account',
};

export const addExpenseErrorMessage = (t, error) => {
  const body = error?.response?.data || {};
  if (body.error === 'invoice_expense_invalid') {
    return formatValidateExpenseError(t, body.message);
  }
  return t.read(ERROR_KEYS[body.error] || 'internal_saving_error');
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * XPEN-3551: the "+" on an XPENSE invoice. Collects a whole expense with the
 * review form (same fields, client configuration, validator and rules as the
 * employee's review screen) and creates it with its invoice line in one call,
 * so a line never exists without the expense behind it.
 *
 * Categories are listed for the report owner, not for the person typing.
 */
const AddInvoiceExpenseModal = ({ data, invoiceId, invoice, billable, onClose, onSaved }) => {
  const defaultTaxCode = useMemo(
    () => data.config?.tax_profile?.find((profile) => profile.is_default === true)?.code ?? 'N_IN',
    [data.config?.tax_profile]
  );

  // A blank receipt in the report's currency, client or internal depending on
  // which table's "+" opened the modal (same rule as the invoice editor's addItem).
  const [expenseData] = useState(() => ({
    json: {
      type: 'receipt',
      Currency: invoice?.json?.Currency,
      InvoiceDate: today(),
      ExpenseType: billable ? 'client' : 'internal',
      IsBillable: Boolean(billable),
      ListItem: [],
    },
  }));

  // The report owner. Without one, ExpenseComponent would list the accountant's own
  // categories (null means "the caller"), so leave the list unloaded (undefined)
  // and say why instead.
  const ownerId = resolvePrincipalUserIdFromPayloadUser(invoice?.user) ?? undefined;
  const [saving, setSaving] = useState(false);
  const [dialogElement, setDialogElement] = useState(null);
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [catalogError, setCatalogError] = useState(
    () => (ownerId === undefined ? data.t.read('invoice.add_expense.error.catalog_unavailable') : null)
  );

  const {
    expense,
    handleUpdateExpense,
    mainInfo,
    setMainInfo,
    validationErrors,
    setValidationErrors,
    ruleAlerts,
    handleExpenseValidationChange,
    handleImputationLeafStageChange,
    handleImputationRowsChange,
    prepareSubmission,
  } = useExpenseReviewForm({ data, expenseData, defaultTaxCode });

  const handleCatalogError = useCallback(() => {
    setCatalogError(data.t.read('invoice.add_expense.error.catalog_unavailable'));
  }, [data.t]);

  // The modal uses Expense name for the review form's narrative value. The
  // review screen still owns its separate Description field.
  const handleNameChange = useCallback((value) => {
    setName(value);
    handleUpdateExpense({ info: { narrative: value.trim() } });
  }, [handleUpdateExpense]);

  // One date is shown in this modal. Keep InvoiceDate in the payload aligned
  // with the Expense date the user edits.
  const handleExpenseDateChange = useCallback((date) => {
    setMainInfo((previous) => ({ ...previous, date }));
  }, [setMainInfo]);

  const showExpenseDate = Utils.hasExpenseField(data, 'field_expense_date');
  const showInvoiceNumber = Utils.shouldShowExpenseReviewInvoiceNumber(data);

  const save = useCallback(async () => {
    if (saving || catalogError) {
      return;
    }
    if (!name.trim()) {
      setNameTouched(true);
      return;
    }
    setSaving(true);
    const payload = await prepareSubmission();
    if (!payload) {
      setSaving(false);
      return;
    }
    fetch(
      `/invoices/${invoiceId}/expenses`,
      'POST',
      { json: { ...payload.json, Description: name.trim() } },
      (response) => onSaved(response?.data?.checksum),
      (error) => {
        setSaving(false);
        setValidationErrors([addExpenseErrorMessage(data.t, error)]);
      }
    );
  }, [saving, catalogError, name, prepareSubmission, invoiceId, onSaved, setValidationErrors, data.t]);

  return (
    <Dialog
      isOpen
      onClose={onClose}
      closeDisabled={saving}
      closeLabel={data.t.read('close')}
      labelledBy='add-invoice-expense-title'
      onElement={setDialogElement}
      additionnalStyles='add-invoice-expense-dialog'
    >
      <form noValidate onSubmit={(e) => { e.preventDefault(); save(); }} data-testid='add-invoice-expense-form'>
        <h2 id='add-invoice-expense-title' className='h4 mb-3'>{data.t.read('invoice.add_expense.title')}</h2>
        <p className='text-muted small'>{data.t.read('invoice.add_expense.intro')}</p>
        {catalogError && (
          <div className='alert alert-danger' data-testid='add-invoice-expense-catalog-error'>
            {catalogError}
          </div>
        )}
        <div className='form-group'>
          <label htmlFor='add-invoice-expense-name'>
            {data.t.read('invoice.add_expense.name')} <span className='text-danger'>*</span>
          </label>
          <input
            id='add-invoice-expense-name'
            className={`form-control ${nameTouched && !name.trim() ? 'is-invalid' : ''}`}
            value={name}
            onChange={(event) => handleNameChange(event.target.value)}
            onBlur={() => setNameTouched(true)}
            required
            aria-invalid={nameTouched && !name.trim()}
            aria-describedby={nameTouched && !name.trim() ? 'add-invoice-expense-name-error' : undefined}
          />
          {nameTouched && !name.trim() && (
            <div id='add-invoice-expense-name-error' className='invalid-feedback'>
              {data.t.read('form_errors.required_field')}
            </div>
          )}
        </div>
        {(showInvoiceNumber || !showExpenseDate) && <MainInfo
          mainInfo={mainInfo}
          setMainInfo={setMainInfo}
          showInvoiceNumber={showInvoiceNumber}
          hideInvoiceDate={showExpenseDate}
          forceDateEditable={!showExpenseDate}
          onInvoiceDateChange={(date) => handleUpdateExpense({ date })}
        />}
        <ExpenseComponent
          expenseIndex={0}
          expense={expense}
          updateInfo={handleUpdateExpense}
          onValidationChange={handleExpenseValidationChange}
          onImputationLeafStageChange={handleImputationLeafStageChange}
          onImputationRowsChange={handleImputationRowsChange}
          imputationReviewForUserId={ownerId}
          onImputationCatalogError={handleCatalogError}
          allowMileage={false}
          allowMatterSplit={false}
          chosenExpenseType={billable ? 'client' : 'internal'}
          showHeading={false}
          onExpenseDateChange={handleExpenseDateChange}
          hideNarrative
          compactOptionalFields
          menuPortalTarget={dialogElement}
        />
        <ExpenseReviewAlerts validationErrors={validationErrors} ruleAlerts={ruleAlerts} />
        <div className='add-invoice-expense-actions'>
          <button type='button' className='btn btn-outline-secondary' onClick={onClose} disabled={saving}>
            {data.t.read('cancel')}
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving || catalogError != null}>
            {saving ? data.t.read('please_wait') : data.t.read('save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default inject('data')(AddInvoiceExpenseModal);
